import { useCallback, useEffect, useRef, useState } from 'react';
import { MdFilterList } from 'react-icons/md';
import './ColumnFilterHeader.css';

export function isColumnFilterActive(filters, key, emptyValue = '') {
  const value = filters?.[key];
  if (emptyValue !== '' && emptyValue != null) {
    return value != null && value !== emptyValue;
  }
  return Boolean(String(value ?? '').trim());
}

export function matchTextFilter(value, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return String(value ?? '').toLowerCase().includes(needle);
}

export function matchSelectFilter(value, filter, emptyValue = 'all') {
  if (!filter || filter === emptyValue) return true;
  return String(value ?? '') === String(filter);
}

export function matchAmountFilter(value, query, formatAmount) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  const amount = Number(value) || 0;
  const amountText = String(amount);
  const formatted = typeof formatAmount === 'function'
    ? String(formatAmount(amount)).toLowerCase()
    : '';
  return amountText.includes(needle) || (formatted && formatted.includes(needle));
}

export function matchGymIdFilter(value, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  const digits = needle.replace(/\D/g, '');
  const gymId = String(value ?? '').toLowerCase();
  const gymDigits = gymId.replace(/\D/g, '');
  return gymId.includes(needle) || (digits && gymDigits.includes(digits));
}

/** Pakistani mobile style: 0300-1234567 (separator after first 4 digits). */
export function formatPhoneFilterValue(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}-${digits.slice(4)}`;
}

/**
 * Date filter input: day → month → year with auto dashes (DD-MM-YYYY).
 * Typing 04082026 → 04-08-2026
 * Also accepts spaced / slashed entry: "3 8 2026" / "3/8/2026" → 03-08-2026
 */
export function formatDateFilterValue(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Preserve intentional D M Y entry with separators (space, /, ., -)
  const loose = raw.match(/^(\d{1,2})[\s/.-]+(\d{1,2})(?:[\s/.-]+(\d{0,4}))?$/);
  if (loose) {
    const day = loose[1];
    const month = loose[2];
    const year = loose[3] || '';
    if (!year) return `${day.padStart(2, '0')}-${month.padStart(2, '0')}`;
    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year.slice(0, 4)}`;
  }

  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (!digits) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

/**
 * Normalize a date filter value to YYYY-MM-DD for DB queries.
 * Accepts DD-MM-YYYY, DD MM YYYY, D/M/YYYY, DD-MM-YY, or YYYY-MM-DD.
 * Returns '' for incomplete or invalid dates (so we don't query while typing).
 */
export function normalizeDateFilterToIso(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === 'all' || raw === '—') return '';

  let year;
  let month;
  let day;

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    year = Number(raw.slice(0, 4));
    month = Number(raw.slice(5, 7));
    day = Number(raw.slice(8, 10));
  } else {
    const dmy = raw.match(/^(\d{1,2})[\s/.-]+(\d{1,2})[\s/.-]+(\d{4})$/);
    if (dmy) {
      day = Number(dmy[1]);
      month = Number(dmy[2]);
      year = Number(dmy[3]);
    } else {
      const dmyShort = raw.match(/^(\d{1,2})[\s/.-]+(\d{1,2})[\s/.-]+(\d{2})$/);
      if (!dmyShort) return '';
      day = Number(dmyShort[1]);
      month = Number(dmyShort[2]);
      const yy = Number(dmyShort[3]);
      year = yy >= 70 ? 1900 + yy : 2000 + yy;
    }
  }

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return '';
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return '';

  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const parsed = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  if (parsed.getFullYear() !== year || parsed.getMonth() + 1 !== month || parsed.getDate() !== day) {
    return '';
  }
  return iso;
}

/** Display empty/null cells consistently for select filters. */
export function cellValue(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

/** Unique sorted values from a column for dropdown filters (capped for large lists). */
export function uniqueColumnOptions(rows, getValue, { max = 200 } = {}) {
  const values = new Set();
  for (const row of rows || []) {
    const value = getValue(row);
    if (value == null || value === '') {
      values.add('—');
    } else {
      values.add(String(value));
    }
    if (values.size > max) break;
  }
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
}

export function useColumnFilters(initialFilters) {
  const [filters, setFilters] = useState(initialFilters);
  const [openFilterKey, setOpenFilterKey] = useState(null);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleFilter = useCallback((key) => {
    setOpenFilterKey(key);
  }, []);

  return {
    filters,
    setFilters,
    updateFilter,
    openFilterKey,
    setOpenFilterKey,
    toggleFilter,
  };
}

export function ColumnFilterHeader({
  label,
  filterKey,
  openKey,
  onToggle,
  filters,
  onFilterChange,
  options = null,
  allLabel = 'All',
  emptyValue = '',
  placeholder = 'Filter...',
  formatValue = null,
}) {
  const selectEmpty = options ? (emptyValue || 'all') : emptyValue;
  const active = isColumnFilterActive(filters, filterKey, options ? selectEmpty : '');
  const open = openKey === filterKey;
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onToggle(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onToggle]);

  const handleTextChange = (raw) => {
    const next = typeof formatValue === 'function' ? formatValue(raw) : raw;
    onFilterChange(filterKey, next);
  };

  return (
    <th className={`col-filter-th${active ? ' is-filtered' : ''}${open ? ' is-open' : ''}`}>
      <div className="col-filter" ref={panelRef}>
        <button
          type="button"
          className="col-filter-trigger"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(open ? null : filterKey);
          }}
          aria-expanded={open}
          title={`Filter by ${label}`}
        >
          <span>{label}</span>
          <MdFilterList className={`col-filter-icon${active ? ' active' : ''}`} aria-hidden />
        </button>
        {open && (
          <div className="col-filter-panel" onClick={(e) => e.stopPropagation()}>
            {options ? (
              <select
                className="owner-form-select col-filter-input"
                value={filters[filterKey] ?? selectEmpty}
                onChange={(e) => onFilterChange(filterKey, e.target.value)}
                autoFocus
              >
                <option value={selectEmpty}>{allLabel}</option>
                {options.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="owner-form-input col-filter-input"
                placeholder={placeholder}
                value={filters[filterKey] ?? ''}
                onChange={(e) => handleTextChange(e.target.value)}
                inputMode={formatValue ? 'numeric' : undefined}
                autoFocus
              />
            )}
            {active && (
              <button
                type="button"
                className="owner-btn owner-btn-secondary owner-btn-sm col-filter-clear"
                onClick={() => onFilterChange(filterKey, options ? selectEmpty : '')}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    </th>
  );
}

export default ColumnFilterHeader;
